import { pluralCategory } from '@/i18n/format.ts'
import type { Messages } from '@/i18n/messages/en/index.ts'

/**
 * `admin.studio` in Brazilian Portuguese.
 *
 * ── What stays in English, and why that is the point (D-15) ──
 *
 * `prompt`, `system prompt`, `top-k`, `TTL`, `wall clock`, `alias`, `slug`, `JSON` and `id`.
 * This is the administrator plane and AGM-022 entitles an operator to the technical name of
 * the thing they are running; a Brazilian engineer meets every one of these in English in
 * every other tool on their screen, and a translated `wall clock` would send them looking
 * for a setting that does not exist.
 *
 * ── The one English word this file refuses ──
 *
 * The retrieval rows say *sources* where they mean pinned knowledge bases. *Fonte* is
 * reserved for a citation in this product's vocabulary and must never mean a base
 * (ADR-0018), so those entries read **bases** and **bases fixadas**. Following the English
 * here would have quietly introduced the one ambiguity the glossary exists to prevent.
 *
 * ── Agreement ──
 *
 * A *definição* is feminine, so the layer card badges read *desativada*, *não salva*,
 * *ativada*. A *ferramenta* is feminine (*embutidas*, *conectadas*). An *agente* is
 * masculine. `preview.none` is the awkward one: it labels an empty list of tools (f), of
 * remote agents (m) and of risk classes (m) from a single entry, so it is written as **nada**
 * — a word with no agreement to get wrong (PRD-27 D-21).
 */
export const studio: Messages['admin']['studio'] = {
  tabs: {
    reports: 'Denúncias',
    systems: 'Sistemas',
    capabilities: 'Capacidades',
    policies: 'Perfis'
  },

  // As mesmas seis palavras de `approvals`, `trace`, `governance` e `activity`: o inglês é o
  // mesmo mapa nas cinco, e concordam com a *ação* classificada, que é feminina.
  riskLabel: {
    read: 'leitura',
    reversible_write: 'escrita reversível',
    external_communication: 'comunicação externa',
    privileged: 'privilegiada',
    financial: 'financeira',
    destructive: 'destrutiva'
  },

  yes: 'sim',
  no: 'não',

  page: {
    adminOnly: 'Acesso de administrador necessário',
    adminOnlyBody: 'Esta tela mostra como cada agente da plataforma está configurado.',
    noneSelected: 'Nenhum agente selecionado',
    noneSelectedLoading: 'Carregando os agentes configurados…',
    noneSelectedBody:
      'Escolha um agente à esquerda, ou crie um — um nome, um prompt e as ferramentas que ele pode alcançar.'
  },

  list: {
    title: 'Agentes',
    new: 'Novo',
    loading: 'Carregando os agentes…',
    empty:
      'Nenhum agente configurado. Um agente é configuração guardada — um prompt, uma concessão de ferramentas e um orçamento — não um deploy.',
    // A badge beside a truncated name: *inativo* is the short idiomatic form, and *desligado*
    // would push the row's name into an ellipsis at 290 px.
    off: 'inativo',
    rowMeta: (version, when) => `v${version} · ${when}`,

    create: {
      idLabel: 'Id do agente',
      idPlaceholder: 'support-triage',
      idTaken: 'já existe um agente com este id',
      idPattern: 'letras minúsculas, números e hifens, de 2 a 63 caracteres',
      nameLabel: 'Nome de exibição',
      namePlaceholder: 'o padrão é o id',
      promptLabel: 'System prompt',
      promptPlaceholder: 'Quem é este agente e o que ele pode presumir.',
      submit: 'Criar',
      cancel: 'Cancelar',
      note: 'criado no estúdio de agentes'
    }
  },

  editor: {
    loading: 'Carregando o agente…',
    failed: 'Não foi possível carregar este agente',
    retry: 'Tentar novamente',
    versionLabel: 'versão',
    readOnly: 'somente leitura — é preciso o papel de administrador para salvar',
    noDefinition: 'Este agente não tem definição',
    noDefinitionBody: 'As linhas dele podem ter sido excluídas. Restaure pelo histórico de versões, ou crie de novo.',

    toolNote: (riskLabel, needsApproval) => (needsApproval ? `${riskLabel} · aprovação` : riskLabel),
    // A note under a checkbox label, so it takes the abbreviation Brazilian interfaces use
    // rather than the full *documentos*, which would wrap the option onto a third line.
    documentCount: (fmt, count) =>
      pluralCategory(fmt.locale, count) === 'one'
        ? `${fmt.formatCount(count)} doc.`
        : `${fmt.formatCount(count)} docs.`,
    unknownTool: 'fora do registro de ferramentas',

    expand: 'Expandir a definição',
    collapse: 'Recolher a definição',
    definition: 'Definição',
    disabledBadge: 'desativada',
    unsavedBadge: 'não salva',
    savingBadge: 'salvando',
    enabledLabel: 'ativada',
    layerMeta: (version, overrides) => `v${version} · igual para todas as pessoas que podem usar · ${overrides}`,
    overridesNothing: 'não sobrescreve nada',
    overridesSets: (fields) => `define ${fields}`,

    sections: {
      identity: 'Identidade',
      instructions: 'Instruções',
      model: 'Modelo',
      tools: 'Ferramentas',
      retrieval: 'Recuperação',
      memory: 'Memória',
      budget: 'Orçamento',
      delegation: 'Delegação',
      metadata: 'Metadados'
    },

    displayNameLabel: 'nome de exibição',
    descriptionLabel: 'descrição',
    descriptionPlaceholder: 'para que serve este agente',

    promptLabel: 'system prompt',
    promptPlaceholder: 'No mínimo, o agente é um prompt. Escreva ele aqui.',
    promptMissing: 'nenhum prompt definido — o agente não vai resolver',
    charCount: (fmt, count) =>
      pluralCategory(fmt.locale, count) === 'one'
        ? `${fmt.formatCount(count)} caractere`
        : `${fmt.formatCount(count)} caracteres`,
    prefixLabel: 'prefixo do prompt',
    prefixPlaceholder: 'uma instrução permanente, ex.: sempre cite o número do chamado interno',
    prefixHint:
      'uma instrução permanente colocada antes do prompt em toda execução, depois de qualquer uma que a instalação definir',

    modelAliasLabel: 'alias do modelo',
    answerModeLabel: 'modo de resposta',

    builtinToolsLabel: 'ferramentas embutidas',
    builtinToolsHint:
      'uma lista de permissão — uma ferramenta que o agente não recebeu não pode ser alcançada, seja qual for o prompt',
    connectedToolsLabel: 'ferramentas conectadas',
    connectedToolsHint: 'nomes qualificados pelo servidor, ex.: jira/create_issue',
    connectedToolsPlaceholder: 'servidor/ferramenta',

    sourcesLabel: 'bases fixadas',
    sourcesHint: 'em branco significa todas as bases que a pessoa pode ler',

    memoryReadLabel: 'leitura',
    memoryWriteLabel: 'escrita',
    memoryWrite: {
      off: 'desligada',
      propose: 'proposta',
      auto: 'automática'
    },

    metadataLabel: 'metadados do operador (objeto json)',
    metadataPlaceholder: '{}',
    metadataNotObject: 'os metadados precisam ser um objeto JSON',
    metadataNotJson: 'ainda não é um JSON válido',

    historyShow: 'Histórico de versões',
    historyHide: 'Esconder o histórico',
    historyLoading: 'Carregando o histórico…',
    historyEmpty: 'Nenhuma versão registrada.',
    historyNoNote: 'sem nota',
    historyLoad: 'Carregar',

    staleTitle: 'Outra pessoa salvou esta camada primeiro',
    staleBody: (version) =>
      `Você editou a versão ${version} e a camada guardada já passou dela. Nada foi sobrescrito. A sua cópia continua na tela — copie o que precisar dela e então recarregue para partir da versão que está lá.`,
    staleReload: 'Recarregar esta camada',
    staleKeep: 'Continuar editando',

    changeNotePlaceholder: 'o que mudou e por quê — fica no histórico de versões',
    changeNoteLabel: 'Nota da mudança',
    savedNote: 'salvo',
    revert: 'Reverter',
    save: 'Salvar',

    platformDefault: 'padrão da plataforma',
    inheritOption: (inherited) => `herdar · ${inherited}`,

    listInherited: 'herdado · sobrescrever',
    listOverriding: 'sobrescrevendo · voltar a herdar',
    listFromLower: 'herdado de uma camada abaixo',
    listEmpty: 'nada concedido',
    listRemove: (value) => `Remover ${value}`,
    listAddTo: (label) => `Adicionar a ${label}`,
    listAdd: 'Adicionar',

    budgetFields: {
      maxSteps: 'máx. de etapas',
      maxModelCalls: 'máx. de chamadas ao modelo',
      maxToolCalls: 'máx. de chamadas de ferramenta',
      maxDelegations: 'máx. de delegações',
      maxCostUsd: 'custo máx. (usd)',
      maxWallClockMs: 'máx. de wall clock (ms)'
    },
    delegationFields: {
      maxDepth: 'profundidade máx.',
      maxParallel: 'paralelo máx.',
      remoteAgents: 'agentes remotos',
      remoteAgentsHint: 'slugs do registro para os quais este agente pode passar trabalho',
      remoteAgentsPlaceholder: 'slug do agente remoto'
    },

    overrideFields: {
      systemPrompt: 'prompt',
      systemPromptPrefix: 'prefixo',
      modelAlias: 'modelo',
      answerMode: 'modo de resposta',
      tools: 'ferramentas',
      retrieval: 'recuperação',
      memory: 'memória',
      budget: 'orçamento',
      delegation: 'delegação',
      meta: 'metadados'
    }
  },

  preview: {
    title: 'Agente resolvido',
    resolving: 'resolvendo',
    degraded: 'degradado',
    lead: 'Sob o que toda execução deste agente roda — igual para todas as pessoas que podem usar.',
    failed: 'Este agente não resolve',
    loading: 'Resolvendo…',
    empty: 'Nada resolvido ainda',
    emptyBody: 'A requisição não começou. Verifique a conexão e tente de novo.',

    definitionEyebrow: 'definição',
    platformOnly: 'só a plataforma',
    policyEyebrow: 'política',
    versionBadge: (version) => `v${version}`,
    policyVersion: (name, version) => `${name} v${version}`,

    showPrompt: 'Mostrar o prompt montado',
    hidePrompt: 'Esconder o prompt montado',

    none: 'nada',
    everySource: 'todas as bases que a pessoa pode ler',
    pinned: (fmt, count) =>
      pluralCategory(fmt.locale, count) === 'one'
        ? `${fmt.formatCount(count)} fixada`
        : `${fmt.formatCount(count)} fixadas`,
    charCount: (fmt, count) =>
      pluralCategory(fmt.locale, count) === 'one'
        ? `${fmt.formatCount(count)} caractere`
        : `${fmt.formatCount(count)} caracteres`,

    sections: {
      instructions: 'Instruções',
      model: 'Modelo',
      tools: 'Ferramentas',
      retrieval: 'Recuperação',
      memory: 'Memória',
      budget: 'Orçamento',
      humanInLoop: 'Supervisão humana',
      delegation: 'Delegação'
    },

    rows: {
      prompt: 'prompt',
      prefix: 'prefixo',
      alias: 'alias',
      answerMode: 'modo de resposta',
      builtin: 'embutidas',
      connected: 'conectadas',
      strategy: 'estratégia',
      topK: 'top-k',
      minScore: 'score mínimo',
      sources: 'bases',
      memoryRead: 'leitura',
      memoryWrite: 'escrita',
      steps: 'etapas',
      modelCalls: 'chamadas ao modelo',
      toolCalls: 'chamadas de ferramenta',
      delegations: 'delegações',
      cost: 'custo',
      wallClock: 'wall clock',
      extraRisks: 'riscos extras',
      rounds: 'ciclos',
      ttl: 'ttl',
      remoteAgents: 'agentes remotos',
      maxDepth: 'profundidade máx.',
      maxParallel: 'paralelo máx.'
    },

    originPolicy: (name, version) => `política ${name} v${version}`,
    originCapped: (name, version) => `limitado pela política ${name} v${version}`,
    originConcatenated: 'concatenado',
    originPlatform: 'plataforma',
    originDefinition: 'definição'
  },

  /** A moldura do estúdio e sua aba de curadoria. Termos técnicos ficam em inglês (D-15). */
  frame: {
    title: 'Estúdio de agentes',
    lead: 'Definições, curadoria e a política do marketplace que rege as duas.',
    adminOnly: 'Acesso de administração necessário',
    adminOnlyBody:
      'Esta tela decide o que cada agente da plataforma pode fazer, e quem pode publicar um. É restrita a quem administra.',
    tabs: {
      definitions: 'Definições',
      curation: 'Curadoria',
      settings: 'Configurações'
    }
  },

  curation: {
    title: 'Publicações',
    lead: 'Ordem em destaque, o selo Oficial e a suspensão. Nada disso é acionável por um pedido de quem cria.',
    loading: 'Carregando publicações…',
    empty: 'Nenhuma publicação ainda',
    emptyBody: 'Publicações e rascunhos aparecem aqui conforme as pessoas os criam.',
    unavailable: 'O índice de publicações ainda não está disponível nesta instalação.',
    unavailableBody:
      'GET /api/admin/marketplace/listings não foi implementado — as ações de curadoria estão desativadas.',
    featureRank: 'posição',
    featureClear: 'Desafixar',
    officialOn: 'Conceder Oficial',
    officialOff: 'Revogar Oficial',
    suspend: 'Suspender',
    unsuspend: 'Retirar suspensão',
    suspendTitle: (name) => `Suspender ${name}`,
    suspendLead:
      'A suspensão bloqueia novas execuções em toda a plataforma, inclusive pela API, dentro do SLO de 60 segundos.',
    reasonPublic: 'Motivo mostrado às pessoas (seguro para quem usa)',
    reasonPublicPlaceholder: 'Este agente está indisponível enquanto verificamos uma coisa.',
    reasonFull: 'Motivo completo (só para quem mantém e para a auditoria)',
    reasonFullPlaceholder: 'O que aconteceu, o que você verificou e o que precisa mudar antes de ele voltar.',
    replacement: 'Id do agente substituto (opcional)',
    replacementPlaceholder: 'expense-buddy-v2',
    confirmSuspend: 'Suspender agora',
    cancel: 'Cancelar',
    failed: 'Essa ação falhou.',
    needsOwnerTitle: 'Precisa de responsável',
    needsOwnerLead:
      'Ninguém mantém mais estes — quem cuidava saiu, ou o grupo ficou sem gestão. Eles continuam atendendo até terem responsável.'
  }
}
